import { unstable_rethrow } from "next/navigation";
import { AppError, ERRORS_COPY, HttpError, toUserMessage } from "@tami/core";

import {
  parseEntitlementError,
  type EntitlementError,
} from "@/lib/entitlements/parse-entitlement-error";

/** Resultado de falha de uma server action (decisão D2 do Plano B2). */
export type ActionFailure = {
  readonly ok: false;
  readonly message: string;
  readonly status: number | null;
  readonly entitlement?: EntitlementError;
};

export type ActionResult<T> =
  | { readonly ok: true; readonly data: T }
  | ActionFailure;

/**
 * Erro lançado dentro de server action não chega ao cliente como instância: em
 * produção o Next troca a mensagem por uma genérica. A action devolve este
 * resultado, com o texto já traduzido no servidor (D2 do Plano B2).
 *
 * Só erro de domínio (`AppError` — inclusive `HttpError` com mensagem da API)
 * repassa a própria mensagem; qualquer outro (`TypeError`, erro de
 * infraestrutura) cai na reserva do fluxo, com o original só no log do
 * servidor — a mensagem técnica nunca atravessa para a tela (item 2 do
 * conserto da revisão final do Plano B2).
 */
export function actionFailure(error: unknown, fallback: string): ActionFailure {
  const entitlement = parseEntitlementError(error) ?? undefined;
  if (entitlement || error instanceof AppError) {
    return {
      ok: false,
      message: entitlement?.message ?? toUserMessage(error, fallback),
      status: error instanceof HttpError ? error.status : null,
      ...(entitlement ? { entitlement } : {}),
    };
  }
  console.error(error);
  return { ok: false, message: fallback, status: null };
}

export async function runAction<T>(
  fn: () => Promise<T>,
  fallback: string,
): Promise<ActionResult<T>> {
  try {
    return { ok: true, data: await fn() };
  } catch (error) {
    unstable_rethrow(error); // redirect("/sign-in") do container e notFound() seguem o fluxo do Next
    return actionFailure(error, fallback);
  }
}

/**
 * Falha que a server action devolveu (`ok: false`), com o que a tela precisa
 * para decidir: a mensagem já traduzida no servidor, o status e, no limite de
 * plano, o `entitlement` para abrir o convite de upgrade.
 */
export class ActionError extends Error {
  readonly status: number | null;
  readonly entitlement: EntitlementError | undefined;

  constructor(failure: ActionFailure) {
    super(failure.message);
    this.name = "ActionError";
    this.status = failure.status;
    this.entitlement = failure.entitlement;
  }
}

/**
 * No cliente, resolve o `ActionResult` de uma server action num valor, ou
 * lança. Falha comum (`ok: false`) vira `ActionError` com a mensagem que o
 * servidor já calculou; a rejeição da própria chamada (rede, deploy — a
 * requisição não chegou a rodar a action) não carrega mensagem da API, então
 * vira um `Error` com o genérico e a causa original, sem tocar a rede de
 * novo. Quem chama lê com `toUserMessage(erro, <reserva do fluxo>)`, que troca
 * o genérico pela reserva — a mesma que a tela mostrava antes do B2 (item 1
 * do conserto da revisão final).
 */
export async function unwrapActionResult<T>(
  promise: Promise<ActionResult<T>>,
): Promise<T> {
  let result: ActionResult<T>;
  try {
    result = await promise;
  } catch (cause) {
    throw new Error(ERRORS_COPY.generic, { cause });
  }
  if (!result.ok) throw new ActionError(result);
  return result.data;
}
